import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";
import { useSettings } from "@/providers/SettingsProvider";
import { getFormattedDateTime } from "@/utils/settings";

export interface JobDetails {
  id: string;
  title: string;
  company: {
    id: string;
    name: string;
  };
  description?: string;
  skills: string[];
  created_at: string;
  ai_analysis?: any; // Change to any to handle all possible JSON values
  contactId?: string;
  aiSummary?: string; // Add aiSummary field
  
  // Fields
  employmentType?: string;
  contractDuration?: string;
  startDate?: string;
  endDate?: string;
  seniorityLevel?: string;
  budget?: string;
  budgetFreelance?: string;
  locationType?: string;
  workplaceBreakdown?: string;
  hardSkills?: string[];
  softSkills?: string[];
  educationLevel?: string;
  yearsOfExperience?: string;
  languageRequirements?: string;
  extraLang?: string;
  cultureTags?: string[];
  applicationDeadline?: string;
  jobPostingDate?: string;
  source?: string;
  assignedRecruiter?: string;
  employmentClassification?: string;
}

export const useJobDetail = (jobId: string | undefined) => {
  const [job, setJob] = useState<JobDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [formattedDate, setFormattedDate] = useState<string>("");
  const { dateFormat, timeFormat } = useSettings();

  const fetchJobDetail = async () => {
    if (!jobId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Fetch the job details
      const { data, error } = await supabase
        .from('jobs')
        .select(`
          id,
          title,
          description,
          skills,
          created_at,
          ai_analysis,
          contact_id,
          company_id,
          companies(id, name),
          employment_type,
          contract_duration,
          start_date,
          end_date,
          seniority_level,
          budget,
          budget_freelance,
          location_type,
          workplace_breakdown,
          hard_skills,
          soft_skills,
          education_level,
          years_of_experience,
          language_requirements,
          extra_lang,
          culture_tags,
          application_deadline,
          job_posting_date,
          source,
          assigned_recruiter,
          employment_classification,
          ai_summary
        `)
        .eq('id', jobId)
        .single();

      if (error) {
        console.error("Error fetching job details:", error);
        setLoading(false);
        return;
      }

      // Format the job data
      const formattedJob: JobDetails = {
        id: data.id,
        title: data.title,
        company: {
          id: data.company_id,
          name: data.companies?.name || 'Unknown Company'
        },
        description: data.description ?? '', // Ensure description is always a string
        skills: data.skills || [],
        created_at: data.created_at,
        ai_analysis: data.ai_analysis,
        aiSummary: data.ai_summary || '', // Include the ai_summary field
        contactId: data.contact_id,
        employmentType: data.employment_type,
        contractDuration: data.contract_duration,
        startDate: data.start_date,
        endDate: data.end_date || undefined,
        seniorityLevel: data.seniority_level,
        budget: data.budget,
        budgetFreelance: data.budget_freelance || undefined,
        locationType: data.location_type,
        workplaceBreakdown: data.workplace_breakdown,
        hardSkills: data.hard_skills || [],
        softSkills: data.soft_skills || [],
        educationLevel: data.education_level,
        yearsOfExperience: data.years_of_experience,
        languageRequirements: data.language_requirements,
        extraLang: data.extra_lang || undefined,
        cultureTags: data.culture_tags || [],
        applicationDeadline: data.application_deadline,
        jobPostingDate: data.job_posting_date,
        source: data.source,
        assignedRecruiter: data.assigned_recruiter,
        employmentClassification: data.employment_classification,
      };

      setJob(formattedJob);

      // Format the date using user settings
      if (data.created_at) {
        try {
          // Keep the relative time format for the created_at date
          const date = new Date(data.created_at);
          
          // Check if date is valid before formatting
          if (!isNaN(date.getTime())) {
            const relativeTime = formatDistanceToNow(date, { addSuffix: true });
            setFormattedDate(relativeTime);
          } else {
            setFormattedDate("Invalid date");
          }
        } catch (err) {
          console.error("Error formatting date:", err);
          setFormattedDate("Date error");
        }
      }
      
    } catch (error) {
      console.error("Error fetching job details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobDetail();
  }, [jobId, dateFormat, timeFormat]);

  return {
    job,
    loading,
    formattedDate,
    refetch: fetchJobDetail
  };
};
